import { Link, useNavigate, type LinkProps } from "react-router-dom";

import { cn } from "@/lib/utils";
import { TableRow } from "@/components/ui/table";

interface TableLinkRowProps extends React.ComponentProps<"tr"> {
  to: string;
}

/**
 * A list row that opens `to` when clicked anywhere on it. Its first cell
 * should still hold a real link to the same place (RowLink): that is what
 * keyboard users tab to and what middle-click and "open in new tab" use.
 * Clicks that land on the link, or on any other control in the row, are left
 * to that control.
 */
export function TableLinkRow({
  to,
  className,
  onClick,
  ...props
}: TableLinkRowProps) {
  const navigate = useNavigate();

  return (
    <TableRow
      className={cn("cursor-pointer", className)}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if ((event.target as Element).closest("a, button")) return;
        // A drag that selected text ends in a click too; keep the selection.
        if (window.getSelection()?.toString()) return;
        navigate(to);
      }}
      {...props}
    />
  );
}

/** The row's own link: the entity name, focusable, in the first cell. */
export function RowLink({ className, ...props }: LinkProps) {
  return (
    <Link
      className={cn(
        "rounded-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className
      )}
      {...props}
    />
  );
}
